import { useMatterPersonRoles } from "@/hooks/use-matter-person-roles";
import type { DirectoryUser } from "@/lib/types";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import {
  Badge,
  Button,
  Checkbox,
  DataTable,
  DataTableColumnHeader,
} from "@/components/ui";
import { userTableColumns } from "@/components/features/settings/users/user-table-columns";
import { getUsersOptions } from "@/lib/data/react-query";
import { isEnabledDirectoryUser } from "@/lib/data/requests/firm/firm-user";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { formatUsersName } from "@/lib/utils/format";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

interface SelectUserSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSelectUsers?: (users: DirectoryUser[]) => void;
  onSelectUser?: (user: DirectoryUser) => void;
  multi?: boolean;
  excludeUserIds?: string[];
  matterId?: string;
  title?: string;
  description?: string;
}

const filterSelectableFirmSigners = (
  users: DirectoryUser[],
  excludeUserIds: string[],
) =>
  users.filter(
    (user) => isEnabledDirectoryUser(user) && !excludeUserIds.includes(user.id),
  );

export default function SelectUserModal({
  open,
  setOpen,
  onSelectUsers,
  onSelectUser,
  multi = true,
  excludeUserIds = [],
  title = "Select user",
  description,
  matterId,
}: SelectUserSlideoverProps) {
  const { userRoles } = useMatterPersonRoles(matterId);
  const [selectedUsers, setSelectedUsers] = useState<DirectoryUser[]>([]);
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = useQuery(getUsersOptions(open));

  const users = useMemo(
    () => filterSelectableFirmSigners(usersData?.data ?? [], excludeUserIds),
    [excludeUserIds, usersData?.data],
  );

  useEffect(() => {
    if (!open) {
      setSelectedUsers([]);
    }
  }, [open]);

  const toggleUser = (user: DirectoryUser) => {
    setSelectedUsers((current) =>
      current.some((selected) => selected.id === user.id)
        ? current.filter((selected) => selected.id !== user.id)
        : [...current, user],
    );
  };
  const signerColumns = useMemo<ColumnDef<DirectoryUser>[]>(
    () =>
      userTableColumns
        .filter((column) => column.id !== "Middle Name")
        .flatMap((column): ColumnDef<DirectoryUser>[] =>
          column.id === "Name"
            ? [
                { ...column, size: 260 },
                {
                  id: "Connection label",
                  header: ({ column }) => (
                    <DataTableColumnHeader column={column} />
                  ),
                  size: 220,
                  accessorFn: (user) =>
                    userRoles.get(user.id)?.join(", ") ?? "",
                  cell: ({ row }) => {
                    const roles = userRoles.get(row.original.id);
                    return roles?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {roles.map((role) => (
                          <Badge
                            key={role}
                            variant="secondary"
                            className="font-normal"
                          >
                            {role}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    );
                  },
                },
              ]
            : column.id === "Email"
              ? [
                  {
                    ...column,
                    cell: ({ row }) => (
                      <span className="font-normal">{row.original.email}</span>
                    ),
                  },
                ]
              : [column],
        ),
    [userRoles],
  );
  const columns = useMemo<ColumnDef<DirectoryUser>[]>(
    () =>
      multi
        ? [
            {
              id: "selected",
              header: "",
              size: 48,
              cell: ({ row }) => (
                <Checkbox
                  checked={selectedUsers.some(
                    (user) => user.id === row.original.id,
                  )}
                  aria-label={`Select ${formatUsersName(row.original) || "user"}`}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onCheckedChange={() => {
                    toggleUser(row.original);
                  }}
                />
              ),
            },
            ...signerColumns,
          ]
        : signerColumns,
    [multi, selectedUsers, signerColumns],
  );

  return (
    <AppModal
      size="3xl"
      title={title}
      description={description}
      open={open}
      onOpenChange={setOpen}
      loading={isUsersLoading}
      bodyClassName="px-5 py-4"
      footer={
        multi ? (
          <Button
            type="button"
            className="w-full"
            disabled={selectedUsers.length === 0}
            onClick={() => {
              onSelectUsers?.(selectedUsers);
              setOpen(false);
            }}
          >
            Add selected ({selectedUsers.length})
          </Button>
        ) : undefined
      }
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {isUsersError ? (
          <div className="text-destructive text-sm">Could not load users.</div>
        ) : (
          <DataTable
            entityLabelPlural="users"
            columns={columns}
            data={users}
            isLoading={isUsersLoading}
            isError={isUsersError}
            tableName={tanstackTableNames.matterUserSigners}
            autoResetPageIndex={false}
            onRowClick={(row) => {
              if (multi) {
                toggleUser(row.original);
                return;
              }
              onSelectUser?.(row.original);
              setOpen(false);
            }}
          />
        )}
      </div>
    </AppModal>
  );
}
